<template>
  <form
    :class="[
      'est-form',
      mode === modes.edit || mode === modes.processing ? 'active' : 'inactive',
      readonly ? 'readonly' : ''
    ]"
    @submit="
      $event.preventDefault();
      $emit(events.save);
    "
    :disabled="readonly"
  >
    <div class="form-group est-container">
      <label
        v-if="!noLabel"
        :class="[
          'est-label',
          mode === modes.edit || mode === modes.processing
            ? 'active'
            : 'inactive',
          readonly ? 'readonly' : ''
        ]"
        @click="handleMaybeEditClick"
        :for="id"
        >{{ label }}
        <!-- only show the pencil icon if not readonly -->
        <b-icon-pencil
          v-if="!readonly"
          class="est-pencil-icon"
          @click="handleMaybeEditClick"
        ></b-icon-pencil>
        <div class="est-processing" v-if="mode === modes.processing">
          <b-spinner
            class="est-spinner"
            label="loading"
            small
            variant="secondary"
          ></b-spinner>
          <span class="text-secondary est-processing-text">saving...</span>
        </div></label
      >
      <div
        :class="[
          inlineFlex ? 'd-inline-flex' : 'd-flex',
          'align-content-center est-secondary-container',
          noLabel ? 'inline-icons' : ''
        ]"
      >
        <template v-if="noLabel">
          <b-icon-pencil
            v-if="!readonly"
            class="est-pencil-icon"
            @click="handleMaybeEditClick"
          ></b-icon-pencil>
          <div class="est-processing" v-if="mode === modes.processing">
            <b-spinner
              class="est-spinner"
              label="loading"
              small
              variant="secondary"
            ></b-spinner>
            <span class="text-secondary est-processing-text">saving...</span>
          </div>
        </template>
        <slot></slot>
        <template v-if="mode === modes.edit && true === showButtons" class="">
          <b-button
            variant="primary"
            class="field-action-button est-field-action-save red align-self-center"
            type="submit"
          >
            save
          </b-button>
          <b-button
            variant="link"
            class="field-action-button field-action-cancel align-self-center"
            @click="$emit(events.cancelEdit)"
          >
            cancel
          </b-button>
        </template>
      </div>
      <p class="small muted text-danger">
        <template v-if="showErrorMessage">{{ errorMessage }}</template>
      </p>
    </div>
  </form>
</template>

<script>
import { BButton, BSpinner, BIconPencil } from "bootstrap-vue";
import CreateIdFactory from "../lib/IdGeneratorFactory";
import { AlwaysValidValidator } from "@/components/lib/Validation";

let idGenerator = CreateIdFactory("editable_base_{count}");

const DefaultMode = "defaultMode";
const EditMode = "editMode";
const ProcessingMode = "processingMode";

export const Modes = {
  default: DefaultMode,
  edit: EditMode,
  processing: ProcessingMode
};

const SaveEvent = "save-value";
const NewModeEvent = "new-mode";
const CancelEditEvent = "cancel-edit";
const DeleteEvent = "delete"; // not used in base or most components extending base -- see EditablePhoneNumber

export const Events = {
  save: SaveEvent,
  newMode: NewModeEvent,
  cancelEdit: CancelEditEvent,
  delete: DeleteEvent
};

/**
 * A mixin that sets default behavior for Editable fields extending EditableBase
 * @type {Object}
 */
export const Mixin = {
  data() {
    return {
      id: idGenerator(),
      events: Events,
      modes: Modes,
      value: this.initialValue,
      editValue: this.initialValue,
      errorMessage: null,
      mode: this.initialMode
    };
  },
  props: {
    label: String,
    name: String,
    initialValue: String, // can override type in the inheriting component if necessary
    initialMode: { type: String, default: Modes.default },
    validator: {
      type: Function,
      default: AlwaysValidValidator
    },
    saver: {
      type: Function,
      default: function(resolve) {
        resolve();
      }
    },
    showButtons: { type: Boolean, default: true },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.$refs.base.$on(this.events.newMode, this.handleNewMode);
    this.$refs.base.$on(this.events.cancelEdit, this.handleCancelEdit);
    this.$refs.base.$on(this.events.save, this.handleSave);
  },
  computed: {
    showErrorMessage() {
      return !!this.errorMessage;
    }
  },
  methods: {
    setValue(newValue) {
      this.value = newValue;
      this.editValue = newValue;
    },
    handleMaybeEditClick: function() {
      if (this.mode === this.modes.default && !this.readonly) {
        this.mode = this.modes.edit;
      }
    },
    handleCancelEdit() {
      this.mode = this.modes.default;
      this.setValue(this.value);
      this.errorMessage = null;
    },
    handleNewMode(mode) {
      // can't change modes if readonly!
      if (this.readonly) {
        return;
      }
      this.mode = Object.values(this.modes).includes(mode) ? mode : this.mode;
    },
    /**
     * Override in parent component to customize dirty check in save handler
     * @returns {boolean}
     */
    isDirty() {
      return this.editValue !== this.value;
    },
    /**
     * Processes the editValue before it is passed into saver
     * @param value
     * @returns {string}
     */
    preSaveValueProcessor(value) {
      return value + ""; // coerce to string
    },
    /**
     * Called after a successful save promise
     */
    saveSuccessHandler(response) {
      this.setValue(response.data.newValue);
      this.mode = this.modes.default;
    },
    /**
     * Process promise errors
     * @param errorMessage
     */
    saveCatchErrorHandler(errorMessage) {
      this.mode = this.modes.edit;
      this.errorMessage = errorMessage;
    },
    /**
     * Call the saver function
     * @param resolve
     * @param reject
     */
    callSaver(resolve, reject) {
      this.saver(
        resolve,
        reject,
        this.name,
        this.preSaveValueProcessor(this.editValue)
      );
    },
    preValidateValueProcessor(value) {
      return value;
    },
    cleanupCanceledSaveAttempt() {
      this.mode = this.modes.default;
    },
    handleSave() {
      if (!this.isDirty()) {
        this.cleanupCanceledSaveAttempt();
        return;
      }
      // noinspection JSCheckFunctionSignatures
      let validationValue = this.preValidateValueProcessor(this.editValue);
      let validationResult = this.validator(validationValue);
      if (true === validationResult.isValid) {
        this.errorMessage = null;
        this.mode = this.modes.processing;
        let that = this;
        new Promise(function(resolve, reject) {
          that.callSaver(resolve, reject);
        })
          .then(response => that.saveSuccessHandler(response))
          .catch(errorMessage => that.saveCatchErrorHandler(errorMessage));
      } else {
        this.errorMessage = validationResult.errorMessage;
      }
    }
  }
};

export default {
  name: "EditableBase",
  components: {
    BButton,
    BSpinner,
    BIconPencil
  },
  data() {
    return {
      id: idGenerator(),
      modes: Modes,
      events: Events
    };
  },
  methods: {
    handleMaybeEditClick() {
      if (this.mode === DefaultMode && !this.readonly) {
        this.$emit(Events.newMode, EditMode);
      }
    }
  },
  props: {
    label: String,
    noLabel: { type: Boolean, default: false },
    mode: { type: String, default: Modes.default },
    errorMessage: { type: String, default: null },
    readonly: { type: Boolean, default: false },
    showButtons: { type: Boolean, default: true },
    inlineFlex: { type: Boolean, default: true },
    showErrorMessage: { type: Boolean, default: false }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.est-plaintext:hover,
.est-label.inactive:hover,
.est-pencil-icon:hover {
  cursor: pointer;
}

form.readonly .est-plaintext:hover,
form.readonly .est-label.readonly:hover,
form.readonly .est-pencil-icon:hover {
  cursor: auto;
}

.est-processing {
  position: absolute;
  bottom: -1.25rem;
  transform: scale(0.8);
  transform-origin: 0 50%;
  left: 0;
}
.est-label {
  position: relative;
  display: block;
  padding-left: 1.5rem;
  transition-duration: 0.5s;
}

.est-label.readonly {
  padding-left: 0;
}

.est-label.active {
  padding-left: 0;
}
.est-processing-text {
  margin-left: 1.5em;
  display: inline-block;
}
.est-spinner {
  position: absolute;
  top: 50%;
  margin-top: -0.5em;
}
.est-pencil-icon {
  position: absolute;
  top: 0.25rem;
  left: 0;
  color: #b08463;
  transform: scale(0.8);
  transform-origin: 50% 50%;
  transition-duration: 0.5s;
}

.est-secondary-container {
  position: relative;
  transition-duration: 0.5s;
}

.active .est-pencil-icon {
  transform: scale(0);
  left: -0.8rem;
}

.est-secondary-container.inline-icons {
  padding-left: 1.25rem;
}

.active .est-secondary-container.inline-icons {
  padding-left: 0;
}

.est-field-action-save {
  margin-left: 1rem;
}
</style>
